<template>
  <div class="audio-label" v-loading.fullscreen.lock="loading">
    <div style="flex: 1; width: calc(100% - 195px)">
      <!-- 波形 -->
      <div id="waveform"></div>

      <!-- 音频播放器 -->
      <audio ref="audioRef" :src="fileUrl" controls style="width: 100%" />

      <!-- 时间轴 -->
      <div id="timeline"></div>

      <!-- 缩略图 -->
      <div id="minimap"></div>

      <!-- 标注列表 -->
      <div class="region-list">
        <div
          class="region-item"
          v-for="(item, index) in regionList"
          :key="item.id"
          :style="{ backgroundColor: item.color }"
        >
          <div>{{ index + 1 }}. {{ item.content }}</div>
          <div>
            {{ item.start.toFixed(2) }} - {{ item.end.toFixed(2) }}
            <el-tooltip placement="top-start" trigger="hover" content="播放">
              <el-icon style="cursor: pointer" @click="playRegion(item.region)">
                <VideoPlay />
              </el-icon>
            </el-tooltip>
            <el-tooltip placement="top-start" trigger="hover" content="删除">
              <el-icon
                style="cursor: pointer"
                @click="deleteRegion(item.region)"
                ><Delete
              /></el-icon>
            </el-tooltip>
          </div>
        </div>
      </div>
      <div
        ref="popoverRef"
        class="popover-anchor"
        :style="{ left: menuX + 'px', top: menuY + 'px' }"
      />
      <!-- 右键菜单 -->
      <el-popover
        v-model:visible="popoverVisible"
        :virtual-ref="popoverRef"
        virtual-triggering
        trigger="manual"
        placement="right-start"
        width="120"
      >
        <div class="menu-item" @click="playRegion(clickRegion)">播放</div>
        <div class="menu-item danger" @click="deleteRegion(clickRegion)">
          删除
        </div>
      </el-popover>
    </div>
    <LabelSelect ref="LabelSelectRef" @change="labelChange" />
  </div>
</template>

<script setup>
import LabelSelect from "./LabelSelect.vue";
import { VideoPlay, Delete } from "@element-plus/icons-vue";
import {
  ref,
  onMounted,
  onBeforeUnmount,
  defineEmits,
  defineExpose,
  getCurrentInstance,
} from "vue";
import WaveSurfer from "wavesurfer.js";
import RegionsPlugin from "wavesurfer.js/dist/plugins/regions.esm.js";
import TimelinePlugin from "wavesurfer.js/dist/plugins/timeline.esm.js";
import MinimapPlugin from "wavesurfer.js/dist/plugins/minimap.esm.js";
import { ElMessage } from "element-plus";

const { proxy } = getCurrentInstance();

const emit = defineEmits(["change"]);
const fileUrl = ref("");
const labelValue = ref(null);
const labelChange = (val) => {
  labelValue.value = val;
};
const loading = ref(false);
const open = async (question, answer) => {
  loading.value = true;
  fileUrl.value = question.url;
  regionList.value = [];
  ws.value && ws.value.destroy();
  initWaveSurfer();
  const labels = JSON.parse(question.problem);
  proxy.$refs.LabelSelectRef.setOptions(labels);
  // 等 WaveSurfer 真正 ready 再回显
  ws.value.once("ready", () => {
    if (answer && answer.voiceSegments) {
      answer.voiceSegments.forEach((item) => {
        const params = labels.find((i) => i.id === item.cls);
        const region = regions.value.addRegion({
          start: item.xmin,
          end: item.xmax,
          content: params.label,
          color: hexToRgba(params.color),
        });
        regionList.value.push({
          id: region.id,
          start: region.start,
          end: region.end,
          params: params,
          content: region.content?.textContent || "",
          color: region.color,
          region,
        });
      });
    }

    // ✅ 回显完成后再关闭 loading
    requestAnimationFrame(() => {
      loading.value = false;
      labelValue.value = null;
    });
  });
};
defineExpose({ open });
/* ================= refs ================= */
const answerChange = () => {
  const res = {
    voiceSegments: regionList.value.map((i) => ({
      cls: i.params.id,
      xmin: Number(i.start.toFixed(2)),
      xmax: Number(i.end.toFixed(2)),
    })),
  };

  emit("change", regionList.value.length > 0 ? res : null);
};
const audioRef = ref(null);
const popoverRef = ref(null);

const ws = ref(null);
const regions = ref(null);
const regionList = ref([]);

/* ================= 右键菜单 ================= */

const popoverVisible = ref(false);
const clickRegion = ref(null);
const menuX = ref(0);
const menuY = ref(0);

/* ================= utils ================= */

const hexToRgba = (hex, alpha = 0.8) => {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/* ================= 初始化 ================= */
const collectRegion = (region) => {
  if (!region.content?.textContent && labelValue.value) {
    region.setContent(labelValue.value.label);
    region.setOptions({
      color: hexToRgba(labelValue.value.color),
      className: "drag-select-region",
    });
  }

  regionList.value.push({
    id: region.id,
    start: region.start,
    end: region.end,
    params: labelValue.value,
    content: region.content?.textContent || "",
    color: region.color,
    region,
  });

  // ✅ 只有非初始化阶段才 emit
  if (!loading.value) {
    answerChange();
  }
};
const initWaveSurfer = () => {
  regions.value = RegionsPlugin.create();

  const timeline = TimelinePlugin.create({
    container: "#timeline",
  });

  const minimap = MinimapPlugin.create({
    container: "#minimap",
    height: 60,
    interact: true,
  });

  ws.value = WaveSurfer.create({
    container: "#waveform",
    url: fileUrl.value,
    media: audioRef.value,
    minPxPerSec: 10,
    waveColor: "#c6e2ff",
    progressColor: "#409EFF",
    plugins: [regions.value, timeline, minimap],
  });

  // 框选创建
  regions.value.enableDragSelection({
    color: "rgba(115,118,122,0.4)",
  });

  // 创建区域
  regions.value.on("region-created", (region) => {
    // ✅ 初始化 / 回显阶段：只做数据收集，不做校验
    if (loading.value) {
      // collectRegion(region);
      return;
    }
    // ✅ 用户操作阶段：必须选择标签
    if (!labelValue.value) {
      region.remove();
      ElMessage.warning("请先选择标签");
      return;
    }

    collectRegion(region);
  });

  // 更新区域
  regions.value.on("region-updated", (region) => {
    const item = regionList.value.find((r) => r.id === region.id);
    if (!item) return;

    item.start = region.start;
    item.end = region.end;

    if (!loading.value) {
      answerChange();
    }
  });

  // 点击区域
  regions.value.on("region-clicked", (region, e) => {
    e.preventDefault();
    e.stopPropagation();
    showContextMenu(region, e);
  });
};

/* ================= 行为 ================= */

const showContextMenu = (region, e) => {
  clickRegion.value = region;
  menuX.value = e.clientX;
  menuY.value = e.clientY;
  setTimeout(() => {
    popoverVisible.value = true;
  }, 100);
};

const deleteRegion = (region) => {
  if (!region) return;
  region.remove();
  regionList.value = regionList.value.filter((r) => r.id !== region.id);
  popoverVisible.value = false;
  answerChange();
};

const playRegion = (region) => {
  if (!ws.value || !region) return;

  ws.value.pause();
  ws.value.setTime(region.start);

  setTimeout(() => ws.value.play(), 0);

  const handler = (time) => {
    if (time >= region.end) {
      ws.value.pause();
      ws.value.un("audioprocess", handler);
    }
  };

  ws.value.on("audioprocess", handler);
  popoverVisible.value = false;
};

/* ================= 生命周期 ================= */

onBeforeUnmount(() => {
  ws.value && ws.value.destroy();
});
</script>

<style lang="less" scoped>
.audio-label {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
#waveform,
#timeline,
#minimap {
  margin-top: 8px;
  width: 100%;
}

.region-list {
  margin-top: 16px;
}

.region-item {
  display: flex;
  justify-content: space-between;
  padding: 6px;
  margin-bottom: 6px;
  border-radius: 4px;
  color: #fff;
}

.menu-item {
  padding: 6px;
  cursor: pointer;

  &:hover {
    background: #f5f7fa;
  }

  &.danger {
    color: #f56c6c;
  }
}

.popover-anchor {
  position: fixed;
  width: 0;
  height: 0;
  pointer-events: none;
}

.drag-select-region {
  border-left: none !important;
}
</style>
